import { MAX_FILE_BYTES, PARSE_TIMEOUT_MS } from './limits';
import { startParseWorker } from './startParseWorker';

/**
 * Parse a chosen file in the Worker, killed after 20 s (ruling 10). When no
 * Worker can be started, or it fails before answering, the same parser runs
 * on the main thread from its own chunk, with the same caps; it cannot be
 * killed there, and the 2 MB cap bounds how long it takes.
 */
export const TIMEOUT_MESSAGE = 'This file took too long to read. Try a smaller file, or save it as CSV.';
export const TOO_BIG_MESSAGE = 'The file is larger than 2 MB. Split it into smaller files and import each one.';

/** A File's bytes; FileReader where Blob.arrayBuffer is missing. */
export function readFileBytes(file) {
  if (typeof file.arrayBuffer === 'function') return file.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

const mainThread = () => import('./parseImportFile');

export async function runParse(file, options = {}, deps = {}) {
  const {
    startWorker = startParseWorker,
    loadMainThread = mainThread,
    timeoutMs = PARSE_TIMEOUT_MS,
  } = deps;
  if (file.size > MAX_FILE_BYTES) return { ok: false, error: TOO_BIG_MESSAGE };
  let buffer;
  try {
    buffer = await readFileBytes(file);
  } catch {
    return { ok: false, error: 'Could not read this file.' };
  }
  const onMainThread = async () => {
    try {
      const m = await loadMainThread();
      return await m.parseImportFile(buffer, file.name, options);
    } catch {
      return { ok: false, error: 'Could not read this file.' };
    }
  };

  let worker;
  try {
    worker = startWorker();
  } catch {
    return onMainThread();
  }
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try { worker.terminate(); } catch { /* already gone */ }
      resolve(value);
    };
    const timer = setTimeout(() => finish({ ok: false, error: TIMEOUT_MESSAGE, code: 'timeout' }), timeoutMs);
    worker.onmessage = (e) => finish(e.data);
    // An error in the Worker, or an answer that cannot be read back
    // (messageerror), both fall back to the main thread.
    const fallBack = (e) => {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try { worker.terminate(); } catch { /* already gone */ }
      onMainThread().then(resolve);
    };
    worker.onerror = fallBack;
    worker.onmessageerror = fallBack;
    // The buffer is copied, not transferred, so the fallback can still read it.
    worker.postMessage({ buffer, fileName: file.name, options });
  });
}
